import { PRODUCT_CATALOG } from "~/productCatalog";

type FbqTrackFunction = (
  action: "track" | "trackCustom" | "init" | "consent",
  eventNameOrId: string,
  parameters?: Record<string, unknown>,
) => void;

declare global {
  interface Window {
    fbq?: FbqTrackFunction;
  }
}

type CartLikeItem = {
  id: string;
  quantity?: number;
  price?: number;
  name?: string;
};

function canUseFbq() {
  return typeof window !== "undefined" && typeof window.fbq === "function";
}

function track(eventName: string, parameters?: Record<string, unknown>) {
  if (!canUseFbq()) {
    return;
  }
  window.fbq!("track", eventName, parameters);
}

async function trackAndPause(
  eventName: string,
  parameters?: Record<string, unknown>,
  delayMs = 200,
) {
  track(eventName, parameters);

  if (typeof window === "undefined") {
    return;
  }

  await new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
  });
}

function mapContents(items: readonly CartLikeItem[]) {
  return items.map((item) => {
    const catalogProduct = PRODUCT_CATALOG[item.id];
    return {
      id: item.id,
      quantity: item.quantity ?? 1,
      item_price: item.price ?? catalogProduct?.price ?? 0,
    };
  });
}

function getValue(items: readonly CartLikeItem[]) {
  return items.reduce((sum, item) => {
    const quantity = item.quantity ?? 1;
    const unitPrice = item.price ?? PRODUCT_CATALOG[item.id]?.price ?? 0;
    return sum + unitPrice * quantity;
  }, 0);
}

export function trackAddToCart(item: CartLikeItem) {
  const product = PRODUCT_CATALOG[item.id];
  const value = (item.price ?? product?.price ?? 0) * (item.quantity ?? 1);

  track("AddToCart", {
    content_ids: [item.id],
    content_name: item.name ?? product?.name ?? item.id,
    content_type: "product",
    contents: mapContents([item]),
    currency: "USD",
    value,
  });
}

export function trackViewContent(contentName = "ProblemScape Landing Page") {
  track("ViewContent", {
    content_name: contentName,
  });
}

export async function trackInitiateCheckout(
  items: readonly CartLikeItem[],
  options?: {
    delayMs?: number;
  },
) {
  await trackAndPause(
    "InitiateCheckout",
    {
      content_ids: items.map((item) => item.id),
      content_type: "product",
      contents: mapContents(items),
      currency: "USD",
      num_items: items.reduce((sum, item) => sum + (item.quantity ?? 1), 0),
      value: getValue(items),
    },
    options?.delayMs,
  );
}

export async function trackLead(
  details?: Record<string, unknown>,
  options?: {
    delayMs?: number;
  },
) {
  await trackAndPause(
    "Lead",
    {
      content_name: "ProblemScape",
      content_category: "ESA Purchase Redirect",
      ...details,
    },
    options?.delayMs,
  );
}

export function trackPurchase(
  items: readonly CartLikeItem[],
  options?: {
    transactionId?: string;
  },
) {
  track("Purchase", {
    content_ids: items.map((item) => item.id),
    content_type: "product",
    contents: mapContents(items),
    currency: "USD",
    value: getValue(items),
    ...(options?.transactionId ? { order_id: options.transactionId } : {}),
  });
}
